import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CompanyProvider, useCompany } from "./context/CompanyContext";
import Navbar from "./components/Navbar";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import DashboardAdvanced from "./pages/DashboardAdvanced";
import Employees from "./pages/Employees";
import EmployeeHistory from "./pages/EmployeeHistory";
import EmployeeProfile from "./pages/EmployeeProfile";
import EmployeeMissingRates from "./pages/EmployeeMissingRates";
import Customers from "./pages/Customers";
import Upload from "./pages/Upload";
import Presence from "./pages/Presence";
import Salaries from "./pages/Salaries";
import CompanySalaries from "./pages/CompanySalaries";
import BothSalaries from "./pages/BothSalaries";
import Users from "./pages/Users";
import Enforcement from "./pages/Enforcement";
import Partnerships from "./pages/Partnerships";
import RecruiterAddEmployee from "./pages/RecruiterAddEmployee";
import EmployeeApproval from "./pages/EmployeeApproval";
import SalaryPay from "./pages/SalaryPay";
import Profit from "./pages/Profit";
import GoldCleanReport from "./pages/GoldCleanReport";
import KoralReport from "./pages/KoralReport";
import Report from "./pages/Report";
import Docs from "./pages/Docs";
// import LogsDashboard from "./pages/LogsDashboard";
import "./App.css";
import "./styles/PartnershipThemes.css";

// Protected Route Component
const ProtectedRoute = ({ children, requiredPermission }) => {
  const { isAuthenticated, loading, hasPermission, user } = useAuth();

  if (loading) {
    return (
      <div className='loading-screen'>
        <div className='spinner'></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to='/login' />;
  }

  // Redirect salary_viewer users to salaries page if they try to access other pages
  if (
    user?.permission === "salary_viewer" &&
    window.location.pathname !== "/salaries"
  ) {
    return <Navigate to='/salaries' />;
  }

  // Redirect recruiter users to add-employee page only
  if (
    user?.permission === "recruiter" &&
    window.location.pathname !== "/recruiter/add-employee"
  ) {
    return <Navigate to='/recruiter/add-employee' />;
  }

  // Redirect payer users to salary-pay page only
  if (
    user?.permission === "payer" &&
    window.location.pathname !== "/salary-pay"
  ) {
    return <Navigate to='/salary-pay' />;
  }

  // Check if route requires specific permission
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to='/' />;
  }

  return children;
};

// Layout with Navbar
const Layout = ({ children }) => {
  const { selectedCompany } = useCompany();
  const { user } = useAuth();

  // Apply partnership-specific theme class to body
  React.useEffect(() => {
    // Use selectedCompany (user's active selection) as primary, fallback to user's default
    const partnershipId = selectedCompany?.id || user?.partnership_id;

    // Remove any existing partnership classes
    document.body.classList.remove(
      "partnership-gold-clean",
      "partnership-koral",
    );

    // Add partnership-specific class
    if (partnershipId === 2) {
      document.body.classList.add("partnership-gold-clean");
    } else if (partnershipId === 1) {
      document.body.classList.add("partnership-koral");
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove(
        "partnership-gold-clean",
        "partnership-koral",
      );
    };
  }, [selectedCompany?.id, user?.partnership_id]);

  return (
    <div className='app-layout'>
      <Navbar />
      <main className='main-content'>{children}</main>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <CompanyProvider>
        <Router>
          <Routes>
            <Route path='/login' element={<Login />} />

            <Route
              path='/'
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path='/employees'
              element={
                <ProtectedRoute>
                  <Layout>
                    <Employees />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path='/employees/:employeeId/profile'
              element={
                <ProtectedRoute>
                  <Layout>
                    <EmployeeProfile />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path='/employees/:employeeId/history'
              element={
                <ProtectedRoute>
                  <Layout>
                    <EmployeeHistory />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path='/employees/missing-rates'
              element={
                <ProtectedRoute requiredPermission='admin'>
                  <Layout>
                    <EmployeeMissingRates />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path='/customers'
              element={
                <ProtectedRoute>
                  <Layout>
                    <Customers />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path='/upload'
              element={
                <ProtectedRoute requiredPermission='admin'>
                  <Layout>
                    <Upload />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Logs Dashboard - temporarily disabled
            <Route
              path='/logs'
              element={
                <ProtectedRoute requiredPermission='admin'>
                  <Layout>
                    <LogsDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            */}

            <Route
              path='/presence'
              element={
                <ProtectedRoute>
                  <Layout>
                    <Presence />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path='/salaries'
              element={
                <ProtectedRoute requiredPermission='site_admin'>
                  <Layout>
                    <Salaries />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path='/salaries/company'
              element={
                <ProtectedRoute requiredPermission='site_admin'>
                  <Layout>
                    <CompanySalaries />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path='/salaries/both'
              element={
                <ProtectedRoute requiredPermission='site_admin'>
                  <Layout>
                    <BothSalaries />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path='/users'
              element={
                <ProtectedRoute requiredPermission='admin'>
                  <Layout>
                    <Users />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path='/enforcement'
              element={
                <ProtectedRoute>
                  <Layout>
                    <Enforcement />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path='/dashboard/advanced'
              element={
                <ProtectedRoute requiredPermission='owner'>
                  <Layout>
                    <DashboardAdvanced />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path='/partnerships'
              element={
                <ProtectedRoute requiredPermission='owner'>
                  <Layout>
                    <Partnerships />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path='/recruiter/add-employee'
              element={
                <ProtectedRoute>
                  <Layout>
                    <RecruiterAddEmployee />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path='/employee-approval'
              element={
                <ProtectedRoute requiredPermission='admin'>
                  <Layout>
                    <EmployeeApproval />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path='/salary-pay'
              element={
                <ProtectedRoute>
                  <Layout>
                    <SalaryPay />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path='/profit'
              element={
                <ProtectedRoute>
                  <Layout>
                    <Profit />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path='/report'
              element={
                <ProtectedRoute requiredPermission='admin'>
                  <Layout>
                    <Report />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path='/gold-clean-report'
              element={
                <ProtectedRoute requiredPermission='admin'>
                  <Layout>
                    <GoldCleanReport />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path='/koral-report'
              element={
                <ProtectedRoute requiredPermission='admin'>
                  <Layout>
                    <KoralReport />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path='/docs'
              element={
                <ProtectedRoute>
                  <Layout>
                    <Docs />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route path='*' element={<Navigate to='/' />} />
          </Routes>
        </Router>
      </CompanyProvider>
    </AuthProvider>
  );
}

export default App;
